import { useEffect, useId, useState } from 'react';

import { Button, Divider, Icon, IconButton } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Input, SearchField, Select } from '@ds/forms';
import { moeda } from '@/comuns/formato';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import type { ContatoParaEscolha, NovoOrcamento, Orcamento, Profissional } from '@/data/tipos';

interface ItemEmEdicao {
    /** Vazio quando o item não é um procedimento do catálogo. */
    procedimentoId: string;
    descricao: string;
    quantidade: number;
    valor: string;
    desconto: string;
}

interface ProcedimentoDoCatalogo {
    id: string;
    nome: string;
    precoCentavos: number;
}

const itemVazio = (): ItemEmEdicao => ({ procedimentoId: '', descricao: '', quantidade: 1, valor: '', desconto: '' });

/**
 * "1.234,56" e "1234.56" viram 123456.
 *
 * A recepção digita como está acostumada, e o dado sai inteiro: converter no
 * fim, uma vez, evita o centavo que some quando se soma em ponto flutuante.
 */
export function centavos(valor: string): number {
    const limpo = valor.replace(/\s/g, '').replace(/\.(?=\d{3}\b)/g, '').replace(',', '.');
    return Math.round((Number(limpo) || 0) * 100);
}

const textoDeCentavos = (c: number) => (c ? (c / 100).toFixed(2).replace('.', ',') : '');

/** "Dra. Ana Prado" na proposta e "Ana Prado" na equipe são a mesma pessoa. */
function nomeComparavel(nome: string): string {
    return nome.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/^dra?\.?\s+/, '').trim();
}

function itensDe(origem: Orcamento | undefined): ItemEmEdicao[] {
    return origem && origem.itens.length > 0
        ? origem.itens.map((i) => ({
            procedimentoId: i.procedimentoId ?? '', descricao: i.descricao, quantidade: i.quantidade,
            valor: textoDeCentavos(i.valorUnitarioCentavos), desconto: textoDeCentavos(i.descontoCentavos),
        }))
        : [itemVazio()];
}

export interface NovoOrcamentoModalProps {
    open: boolean;
    onClose: () => void;
    onCriado: () => void;
    /** Paciente já escolhido: quem cria o orçamento a partir da ficha não procura de novo. */
    pacientePadrao?: { id: string; nome: string };
    /**
     * Editar: a proposta carrega no MESMO formulário do novo orçamento — dois
     * formulários para o mesmo objeto divergiriam na primeira mudança.
     */
    orcamento?: Orcamento;
    /** Duplicar: parte de outra proposta, mas nasce orçamento novo, com número novo. */
    base?: Orcamento;
}

export function NovoOrcamentoModal({ open, onClose, onCriado, pacientePadrao, orcamento, base }: NovoOrcamentoModalProps) {
    const aviso = useAviso();
    const idBusca = useId();
    const idResponsavel = useId();
    const idValidade = useId();
    const origem = orcamento ?? base;
    const pacienteDaOrigem = origem?.contatoId ? { id: origem.contatoId, nome: origem.paciente } : null;

    const [paciente, setPaciente] = useState<{ id: string; nome: string } | null>(pacienteDaOrigem ?? pacientePadrao ?? null);
    const [busca, setBusca] = useState('');
    const [resultados, setResultados] = useState<ContatoParaEscolha[] | null>(null);
    const [equipe, setEquipe] = useState<Profissional[] | null>(null);
    const [responsavelId, setResponsavelId] = useState('');
    /* O responsável gravado vem pelo NOME; quando a equipe chega, ele vira o id
       dela — e é contra esse id que "mudou alguma coisa" se mede. */
    const [responsavelDaOrigem, setResponsavelDaOrigem] = useState('');
    const [validade, setValidade] = useState(orcamento?.validade ?? '');
    const [itens, setItens] = useState<ItemEmEdicao[]>(() => itensDe(origem));
    const [catalogo, setCatalogo] = useState<ProcedimentoDoCatalogo[]>([]);
    const [erroCatalogo, setErroCatalogo] = useState<string | null>(null);
    /* Dois erros, porque são dois problemas: faltar paciente e faltar item não
       se resolvem no mesmo lugar da tela. */
    const [erroPaciente, setErroPaciente] = useState<string | null>(null);
    const [erroItens, setErroItens] = useState<string | null>(null);
    const [erroDaEquipe, setErroDaEquipe] = useState<string | null>(null);
    const [salvando, setSalvando] = useState(false);

    const idDoPadrao = pacientePadrao?.id;
    const nomeDoPadrao = pacientePadrao?.nome;

    useEffect(() => {
        if (!open) return;
        const deOrigem = orcamento ?? base;
        setPaciente(deOrigem?.contatoId
            ? { id: deOrigem.contatoId, nome: deOrigem.paciente }
            : idDoPadrao && nomeDoPadrao ? { id: idDoPadrao, nome: nomeDoPadrao } : null);
        setBusca('');
        setResultados(null);
        setResponsavelId('');
        setResponsavelDaOrigem('');
        setValidade(orcamento?.validade ?? '');
        setItens(itensDe(deOrigem));
        setErroPaciente(null);
        setErroItens(null);

        let vivo = true;
        setEquipe(null);
        setErroDaEquipe(null);
        /* Responsável da equipe cadastrada, por id. A lista fixa ("Ana Prado", sem
           "Dra.") nunca batia com o cadastro, e o orçamento saía sem responsável. */
        dados().opcoesDoAgendamento()
            .then((o) => {
                if (!vivo) return;
                setEquipe(o.profissionais);
                const doResponsavel = deOrigem && deOrigem.responsavel !== '—'
                    ? o.profissionais.find((p) => nomeComparavel(p.nome) === nomeComparavel(deOrigem.responsavel))
                    : undefined;
                if (doResponsavel) {
                    setResponsavelId(doResponsavel.id);
                    setResponsavelDaOrigem(doResponsavel.id);
                }
            })
            .catch((e: unknown) => { if (vivo) setErroDaEquipe(e instanceof Error ? e.message : 'Não foi possível ler a equipe.'); });
        return () => { vivo = false; };
    }, [open, idDoPadrao, nomeDoPadrao, orcamento, base]);

    /* O procedimento do item é o que diz, ao aprovar, se nasce pacote. Sem o
       catálogo a escolha não existe — e a tela diz isso em vez de esconder. */
    useEffect(() => {
        if (!open) return undefined;
        let vivo = true;
        dados().procedimentos({ porPagina: 500 })
            .then((carga) => {
                if (!vivo) return;
                setCatalogo(carga.itens.filter((p) => p.ativo).map((p) => ({ id: p.id, nome: p.nome, precoCentavos: p.precoCentavos })));
                setErroCatalogo(null);
            })
            .catch((e: unknown) => {
                if (vivo) setErroCatalogo(e instanceof Error ? e.message : 'Não foi possível carregar os procedimentos.');
            });
        return () => { vivo = false; };
    }, [open]);

    useEffect(() => {
        if (!open || paciente || busca.trim().length < 2) {
            setResultados(null);
            return;
        }
        let vivo = true;
        const t = window.setTimeout(() => {
            dados().buscarContatos(busca)
                .then((r) => { if (vivo) setResultados(r); })
                .catch((e: unknown) => {
                    if (!vivo) return;
                    setResultados(null);
                    setErroPaciente(`A busca falhou: ${e instanceof Error ? e.message : 'tente de novo'}.`);
                });
        }, 250);
        return () => { vivo = false; window.clearTimeout(t); };
    }, [open, busca, paciente]);

    const total = itens.reduce((s, i) => s + centavos(i.valor) * i.quantidade - centavos(i.desconto), 0);

    const alterar = (indice: number, mudanca: Partial<ItemEmEdicao>) => {
        setErroItens(null);
        setItens((atuais) => atuais.map((i, n) => (n === indice ? { ...i, ...mudanca } : i)));
    };

    /* Escolher o procedimento preenche só o que está vazio: quem já digitou a
       descrição ou negociou o valor não perde o que escreveu. */
    const escolherProcedimento = (indice: number, id: string) => {
        const escolhido = catalogo.find((p) => p.id === id);
        setItens((atuais) => atuais.map((i, n) => {
            if (n !== indice) return i;
            return {
                ...i,
                procedimentoId: id,
                descricao: i.descricao.trim() || escolhido?.nome || '',
                valor: i.valor.trim() || (escolhido ? (escolhido.precoCentavos / 100).toFixed(2).replace('.', ',') : ''),
            };
        }));
        setErroItens(null);
    };

    const salvar = async (): Promise<boolean> => {
        const validos = itens.filter((i) => i.descricao.trim());
        if (!paciente) { setErroPaciente('Escolha o paciente na lista.'); return false; }
        if (validos.length === 0) { setErroItens('Inclua ao menos um item.'); return false; }
        /* Item a R$ 0 entrava calado e fechava um orçamento que ninguém cobra. */
        const semValor = validos.find((i) => centavos(i.valor) <= 0);
        if (semValor) { setErroItens(`O item “${semValor.descricao.trim()}” está sem valor.`); return false; }
        const negativo = validos.find((i) => centavos(i.desconto) > centavos(i.valor) * i.quantidade);
        if (negativo) { setErroItens(`O desconto de “${negativo.descricao.trim()}” é maior que o item.`); return false; }

        const responsavel = equipe?.find((p) => p.id === responsavelId);
        const dadosDoOrcamento: NovoOrcamento = {
            contatoId: paciente.id,
            pacienteNome: paciente.nome,
            responsavel: responsavel?.nome ?? '',
            ...(responsavel ? { responsavelId: responsavel.id } : {}),
            validade: validade || undefined,
            itens: validos.map((i) => ({
                ...(i.procedimentoId ? { procedimentoId: i.procedimentoId } : {}),
                descricao: i.descricao.trim(),
                quantidade: i.quantidade,
                valorUnitarioCentavos: centavos(i.valor),
                descontoCentavos: centavos(i.desconto),
            })),
        };

        setSalvando(true);
        try {
            if (orcamento) {
                const salvo = await dados().editarOrcamento(orcamento.id, dadosDoOrcamento);
                aviso({ title: 'Proposta atualizada', description: `${salvo.numero} · ${moeda(salvo.totalCentavos)}` });
            } else {
                const criado = await dados().criarOrcamento(dadosDoOrcamento);
                aviso({ title: 'Orçamento criado', description: `${criado.numero} · ${moeda(criado.totalCentavos)}` });
            }
            onCriado();
            onClose();
            return true;
        } catch (e) {
            setErroItens(e instanceof Error ? e.message : 'Não foi possível salvar o orçamento.');
            return false;
        } finally {
            setSalvando(false);
        }
    };

    const sujo = origem
        ? JSON.stringify({ p: paciente?.id ?? '', r: responsavelId, v: validade, i: itens })
            !== JSON.stringify({ p: pacienteDaOrigem?.id ?? '', r: responsavelDaOrigem, v: orcamento?.validade ?? '', i: itensDe(origem) })
        : (paciente !== null && paciente.id !== idDoPadrao) || busca.trim() !== ''
            || itens.some((i) => i.descricao.trim() !== '' || i.procedimentoId !== '');
    const opcoesDoCatalogo = catalogo.map((p) => ({ value: p.id, label: p.nome }));
    const rotuloDoBotao = orcamento ? 'Salvar proposta' : 'Criar orçamento';
    const responsavelForaDaEquipe = Boolean(origem && origem.responsavel !== '—' && equipe && !responsavelDaOrigem);

    return (
        <Modal
            open={open} onClose={onClose} width={720}
            title={orcamento ? 'Editar proposta' : 'Novo orçamento'}
            description={orcamento
                ? `${orcamento.numero} · o número não muda.`
                : base ? `Cópia de ${base.numero}: nasce como rascunho, com número novo.` : 'O orçamento nasce como rascunho. Enviar é o passo seguinte.'}
            dirty={sujo} onSave={salvar} saveLabel={rotuloDoBotao}
            footer={
                <>
                    <Button variant="secondary" onClick={onClose}>Cancelar</Button>
                    <Button onClick={() => void salvar()} loading={salvando}>{rotuloDoBotao}</Button>
                </>
            }
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {orcamento?.status === 'enviado' && (
                    <Alert tone="warning" title="Esta proposta já foi enviada" dismissible={false}>
                        Salvar a devolve para rascunho: a paciente tem a versão anterior, e a nova precisa ser enviada de novo.
                    </Alert>
                )}
                {erroDaEquipe && <Alert tone="warning" title="A equipe não carregou" dismissible={false}>{erroDaEquipe}</Alert>}
                {responsavelForaDaEquipe && origem && (
                    <Alert tone="warning" title="Responsável fora da equipe cadastrada" dismissible={false}>
                        A proposta foi gravada com {origem.responsavel}, que não está na equipe. Escolha um responsável, ou ela fica sem.
                    </Alert>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    <FormField
                        label="Paciente" required htmlFor={idBusca} error={erroPaciente ?? undefined} style={{ gridColumn: '1 / -1' }}
                        hint={orcamento ? 'A proposta é desta paciente — para outra, duplique' : undefined}
                    >
                        {paciente ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 42, padding: '0 6px 0 12px', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-control)', background: 'var(--surface)' }}>
                                <Icon name="user-round" size={15} style={{ color: 'var(--neutral-500)' }} />
                                <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{paciente.nome}</span>
                                {!orcamento && <Button size="sm" variant="ghost" onClick={() => { setPaciente(null); setBusca(''); }}>Trocar</Button>}
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                <SearchField
                                    value={busca} width="100%" ariaLabel="Buscar paciente" placeholder="Buscar paciente por nome ou telefone"
                                    onChange={(v) => { setBusca(v); setErroPaciente(null); }}
                                />
                                {resultados && resultados.length > 0 && (
                                    <ul aria-label="Contatos encontrados" style={{ listStyle: 'none', margin: 0, padding: 0, border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
                                        {resultados.map((c) => (
                                            <li key={c.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                                                <button
                                                    type="button" onClick={() => { setPaciente({ id: c.id, nome: c.nome }); setResultados(null); }}
                                                    style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 40, padding: '8px 12px', border: 0, background: 'var(--surface)', cursor: 'pointer', textAlign: 'left', fontFamily: 'var(--font-ui)', fontSize: 13.5, color: 'var(--text-primary)' }}
                                                >
                                                    <span style={{ flex: 1, minWidth: 0, fontWeight: 600 }}>{c.nome}</span>
                                                    <span style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>{c.telefone}</span>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {resultados && resultados.length === 0 && (
                                    <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)' }}>Nenhum contato com “{busca.trim()}”. Cadastre em Contatos antes.</p>
                                )}
                            </div>
                        )}
                    </FormField>
                    <FormField label="Responsável" htmlFor={idResponsavel}>
                        <Select
                            id={idResponsavel} value={responsavelId} onChange={setResponsavelId} placeholder="Sem responsável"
                            disabled={!equipe} options={(equipe ?? []).map((p) => ({ value: p.id, label: p.nome }))}
                        />
                    </FormField>
                    <FormField label="Validade" hint="Depois disso o orçamento vence" htmlFor={idValidade}>
                        <Input id={idValidade} type="date" value={validade} onChange={setValidade} />
                    </FormField>
                </div>

                <Divider spacing={2} />

                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <span style={{ fontFamily: 'var(--font-ui)', fontSize: 12, fontWeight: 600, letterSpacing: 'var(--tracking-wide)', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>Itens</span>
                    <span style={{ fontFamily: 'var(--font-ui)', fontSize: 12.5, color: 'var(--text-secondary)' }}>
                        Procedimento com quantidade maior que 1 vira pacote de sessões ao aprovar.
                    </span>
                    {erroCatalogo && <span role="alert" style={{ fontSize: 12.5, color: 'var(--danger-strong)' }}>{erroCatalogo}</span>}

                    {itens.map((item, indice) => (
                        <div key={indice} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1.5fr) 64px 100px 100px 32px', gap: 8, alignItems: 'end' }}>
                            {/* Cada campo tem rótulo próprio, mesmo quando a coluna já
                                se explica: numa linha repetida, "Valor" sem o número
                                do item não diz de qual item se trata para quem ouve. */}
                            <FormField label={indice === 0 ? 'Procedimento' : undefined} htmlFor={`${idBusca}-proc-${indice}`}>
                                <Select
                                    id={`${idBusca}-proc-${indice}`} value={item.procedimentoId}
                                    aria-label={indice === 0 ? undefined : `Procedimento do item ${indice + 1}`}
                                    placeholder="Nenhum" options={opcoesDoCatalogo}
                                    onChange={(v) => escolherProcedimento(indice, v)}
                                />
                            </FormField>
                            <FormField label={indice === 0 ? 'Descrição' : undefined} htmlFor={`${idBusca}-desc-${indice}`}>
                                <Input
                                    id={`${idBusca}-desc-${indice}`} value={item.descricao}
                                    aria-label={indice === 0 ? undefined : `Descrição do item ${indice + 1}`}
                                    onChange={(v) => alterar(indice, { descricao: v })} placeholder="Procedimento ou pacote"
                                />
                            </FormField>
                            <FormField label={indice === 0 ? 'Qtd' : undefined} htmlFor={`${idBusca}-qtd-${indice}`}>
                                <Input
                                    id={`${idBusca}-qtd-${indice}`} type="number" value={String(item.quantidade)}
                                    aria-label={indice === 0 ? undefined : `Quantidade do item ${indice + 1}`}
                                    onChange={(v) => alterar(indice, { quantidade: Math.max(1, Number(v) || 1) })}
                                />
                            </FormField>
                            <FormField label={indice === 0 ? 'Valor unit.' : undefined} htmlFor={`${idBusca}-valor-${indice}`}>
                                <Input
                                    id={`${idBusca}-valor-${indice}`} value={item.valor}
                                    aria-label={indice === 0 ? undefined : `Valor unitário do item ${indice + 1}`}
                                    onChange={(v) => alterar(indice, { valor: v })} placeholder="0,00"
                                />
                            </FormField>
                            <FormField label={indice === 0 ? 'Desconto' : undefined} htmlFor={`${idBusca}-desc-valor-${indice}`}>
                                <Input
                                    id={`${idBusca}-desc-valor-${indice}`} value={item.desconto}
                                    aria-label={indice === 0 ? undefined : `Desconto do item ${indice + 1}`}
                                    onChange={(v) => alterar(indice, { desconto: v })} placeholder="0,00"
                                />
                            </FormField>
                            <IconButton
                                icon={<Icon name="trash-2" size={15} />} label={`Remover item ${indice + 1}`} variant="ghost"
                                disabled={itens.length === 1}
                                onClick={() => setItens((atuais) => atuais.filter((_, n) => n !== indice))}
                            />
                        </div>
                    ))}

                    <Button
                        variant="link" iconLeft={<Icon name="plus" size={14} />}
                        onClick={() => setItens((atuais) => [...atuais, itemVazio()])}
                    >
                        Adicionar item
                    </Button>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 10, borderTop: '1px solid var(--border-subtle)' }}>
                    {erroItens && <span role="alert" style={{ fontSize: 12.5, color: 'var(--danger-strong)' }}>{erroItens}</span>}
                    <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-ui)', fontSize: 13, color: 'var(--text-secondary)' }}>Total</span>
                    <strong style={{ fontFamily: 'var(--font-ui)', fontSize: 18, fontVariantNumeric: 'tabular-nums' }}>{moeda(total)}</strong>
                </div>
            </div>
        </Modal>
    );
}
